import React from 'react';
import PropTypes from 'prop-types';
import { Animator } from '../lib/sprite-animator';
import raf from 'raf';
import styles from './index.css';
import Arrows from './arrows/';
import Dots from './dots/';
import List from './list/';

/**
 * 轮播图item渲染函数，默认渲染一个图片+title+链接，可以通过配置进行覆盖。
 * @param {Object} item 渲染数据。
 * @param {Boolean} isClone 数据是否为clone的。根据该状态，可以对渲染数据中的ref进行处理，防止出现ref覆盖的情况。
 * @param {Number} index 数据的索引号
 */
const sliderTmpl = function sliderTmpl(item, isClone, index) {
    return (
        <a ifeng_ui_pc_sliders-link="" href={item.url} target="_blank">
            <img src={item.src} ifeng_ui_pc_sliders-image="" />
            <div ifeng_ui_pc_sliders-title="">{item.title}</div>
            {item.hasAdIcon && <div className={styles.textMark}>
                <img className={styles.imgMark} src="https://x0.ifengimg.com/feprod/c/feprod/c/ifengimcp/pic/20210108/3677f2773fd79f12b079_size1_w56_h34.png" />
            </div>}
        </a>
    );
};

const dotTmpl = function dotTmpl(item, index) {
    return null;
};

const emptyFn = function emptyFn() {};
const emptyHandleCallback = function emptyHandleCallback() {
    return true;
};

/**
 * @param{Boolean | String} arrows 是否有箭头 默认 true，如果为 hover 的时候，则鼠标放到slider上面显示，移出消失
 * @param{Boolean} dots 是否有导航 默认 true
 * @param{String} dotsAction 导航触发模式 默认 click (click 点击触发 hover 鼠标移入触发)
 * @param{Function} afterChange 滑动完成后的回调函数，传入两个参数 (currentIndex, total)
 * @param{Function} beginChange 滑动开始前的回调函数，传入两个参数 (nexIndex, total)
 * @param{Boolean} autoplay 是否自动播放 默认 true
 * @param{Boolean} isPause 自动播放状态下是否为暂停状态 默认 false 在 autoplay 为 true 的情况下有效
 * @param{String} direction 自动播放时滚动的方向 默认 forward (forward 水平滚动的时候，为从左向右，垂直滚动的时候，为从下到上 reverse 水平滚动的时候，为从右向左，垂直滚动的时候，为从上到下)
 * @param{Boolean} pauseOnHover 自动播放时，鼠标移动上去停止自动播放，移出重新自动播放
 * @param{Number} autoplayInterval 自动播放时间间隔(单位毫秒)，默认 3000
 * @param{String} axis 滚动轴 默认为 horizonta 水平 （horizonta 水平 vertical 垂直）
 * @param{Number} initialSlide 初始显示第几个
 * @param{Number} unitSize 一个slider的单位尺寸 默认 0，如果为 0 时，在组件didmount以后，会根据父容器进行一次获取。
 * @param{Number} speed 滚动速度(单位毫秒) 默认 300，改成零可以实现无动画切换效果
 * @param{Boolean} infinite 是否循环滚动，默认为true，如果允许autoplay，则直接取true
 * @param{Function} sliderTmpl 滑块的模板函数，默认有一个，不过需要传入特定结构的数据，会生成特定的html结构
 * @param{Function} dotTmpl dot的模板函数，默认有一个，是特定的html结构
 * @param{String} dotStyle dot的样式名，默认为空，使用组件自己带的样式（因为 ie8 对属性类样式支持不好，只能传入class）
 * @param{String} dotCurrentStyle dot当前选中的样式名，默认为空，使用组件自己带的样式 （因为 ie8 对属性类样式支持不好，只能传入class）
 * @param{Number} extension 左右扩展数据数量，默认为1，infinite为false的话，值为0，如果展示的图片为多个的话，为了避免尾部出现空白，需要对extension进行配置，平时不需要配置。
 * @param{Number} padding 位移，如果你要展示多个图片，又想让一个居中的话，需要设置此参数，默认为0。
 * @param{Function} nextCallback 点击next按钮的回调，在点击next按钮时，会先触发该回调，如果返回true，则继续next的默认行为，如果返回false，则会阻止next的默认行为（阻止滑动）。传入两个参数（currentIndex, total) currentIndex，默认从0开始
 * @param{Function} preCallback 点击pre按钮的回调，在点击pre按钮时，会先触发该回调，如果返回true，则继续pre的默认行为，如果返回false，则会阻止pre的默认行为（阻止滑动）。传入两个参数（currentIndex, total) currentIndex，默认从0开始
 */
const defaultConfig = {
    arrows: true,
    dots: true,
    dotsAction: 'click',
    afterChange: emptyFn,
    beginChange: emptyFn,
    nextCallback: emptyHandleCallback,
    preCallback: emptyHandleCallback,
    autoplay: true,
    isPause: false,
    direction: 'forward',
    pauseOnHover: true,
    autoplayInterval: 3000,
    axis: 'horizonta',
    initialSlide: 0,
    speed: 300,
    extension: 1,
    padding: 0,
    unitSize: 0,
    infinite: true,
    sliderTmpl,
    dotTmpl,
    dotStyle: '',
    dotCurrentStyle: '',
};

class Slides extends React.PureComponent {
    static propTypes = { config: PropTypes.object, content: PropTypes.array.isRequired };
    static defaultProps = {};

    config = { ...defaultConfig, ...this.props.config };
    autoplayTimer = null;
    isHover = false;
    infinite = this.config.autoplay ? true : this.config.infinite;
    extension = this.config.infinite ? this.config.extension : 0;
    container = React.createRef();

    state = {
        shifting: -(this.config.initialSlide + this.extension - this.config.padding) * this.config.unitSize,
        arrows: this.config.arrows === true,
        index: this.config.initialSlide + this.extension,
        unitSize: this.config.unitSize,
        isPause: this.config.isPause,
    };

    getAnimFrame = () => {
        const { index, unitSize } = this.state;
        const { content } = this.props;
        let nextIndex = index;

        if (this.anim.getPlayState() !== 'finished') {
            raf(this.getAnimFrame);
            this.setState(this.anim.getFrame());
        } else {
            if (index === this.extension - 1) {
                nextIndex = content.length;
            } else if (index === content.length + this.extension) {
                nextIndex = this.extension;
            }

            this.config.afterChange(nextIndex - this.extension, this.props.content.length);
            this.setState({ shifting: -unitSize * (nextIndex - this.config.padding), index: nextIndex });
        }
    };
    componentDidMount() {
        if (this.state.unitSize === 0) {
            const unitSize =
                this.config.axis === 'horizonta'
                    ? this.container.current.parentNode.clientWidth
                    : this.container.current.parentNode.clientHeight;

            this.setState({
                shifting: -(this.config.initialSlide + this.extension - this.config.padding) * unitSize,
                unitSize,
            });
        }

        if (this.config.autoplay) {
            this.autoplayTimer = setInterval(() => {
                if (!this.config.pauseOnHover || !this.isHover) {
                    if (!this.state.isPause) {
                        if (this.config.direction === 'forward') {
                            this.next();
                        } else {
                            this.pre();
                        }
                    }
                }
            }, this.config.autoplayInterval);
        }
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.config.unitSize && nextProps.config.unitSize !== prevState.unitSize) {
            return {
                ...prevState,
                unitSize: nextProps.config.unitSize,
                shifting: -nextProps.config.unitSize * (prevState.index - (nextProps.config.padding || 0)),
            };
        }

        if (nextProps.config.isPause !== prevState.isPause) {
            return {
                ...prevState,
                isPause: nextProps.config.isPause,
            };
        }

        return prevState;
    }

    componentWillUnmount() {
        clearInterval(this.autoplayTimer);
    }
    next = () => {
        const { index, shifting, unitSize } = this.state;
        const { content } = this.props;

        if (!this.config.nextCallback((index - this.extension) % content.length, content.length)) {
            return;
        }

        if (index >= content.length && !this.infinite) {
            return;
        }

        let nextIndex = index;

        if (index >= content.length + this.extension) {
            nextIndex = this.extension;
            this.go(1, nextIndex, shifting + content.length * unitSize);
        } else {
            this.go(1);
        }
    };
    pre = () => {
        const { index, shifting, unitSize } = this.state;
        const { content } = this.props;

        if (!this.config.preCallback((index - this.extension) % content.length, content.length)) {
            return;
        }

        if (index <= 0 && !this.infinite) {
            return;
        }

        let nextIndex = index;

        if (index <= this.extension) {
            nextIndex = content.length + this.extension;
            this.go(-1, nextIndex, shifting - (nextIndex - this.extension) * unitSize);
        } else {
            this.go(-1);
        }
    };
    step = e => {
        const index = e.currentTarget.getAttribute('index') / 1;

        this.go(index - this.state.index);
    };
    go(count, current = this.state.index, positon = this.state.shifting) {
        const nextIndex = current + count;
        const { unitSize } = this.state;
        const { speed } = this.config;
        const total = this.props.content.length;
        let index = 0;

        if (nextIndex - this.extension <= 0) {
            index = 0;
        } else if (nextIndex - this.extension >= total) {
            index = 0;
        } else {
            index = nextIndex - this.extension;
        }

        this.config.beginChange(index, total);

        this.setState(
            {
                index: nextIndex,
            },
            () => {
                this.anim = new Animator(
                    { shifting: positon },
                    [{ shifting: -unitSize * (nextIndex - this.config.padding) }],
                    speed,
                );
                this.anim.play();
                this.getAnimFrame();
            },
        );
    }

    containerMouseEnterHandle = () => {
        if (this.config.arrows === 'hover') {
            this.setState({ arrows: true });
        }

        this.isHover = true;
    };

    containerMouseLeaveHandle = () => {
        if (this.config.arrows === 'hover') {
            this.setState({ arrows: false });
        }

        this.isHover = false;
    };

    render() {
        const { content } = this.props;
        const { shifting, unitSize, index, arrows } = this.state;
        const { dotTmpl, sliderTmpl, axis, dots, dotsAction, dotStyle, dotCurrentStyle } = this.config;
        const sizeType = axis === 'horizonta' ? 'width' : 'height';
        const shiftingType = axis === 'horizonta' ? 'marginLeft' : 'marginTop';

        return content.length > 0 ? (
            <div
                className={styles.box}
                ref={this.container}
                onMouseEnter={this.containerMouseEnterHandle}
                onMouseLeave={this.containerMouseLeaveHandle}>
                {arrows ? (
                    <Arrows
                        nextHandle={this.next}
                        preHandle={this.pre}
                        infinite={this.infinite}
                        extension={this.extension}
                        current={index}
                        total={content.length}
                    />
                ) : null}
                {dots ? (
                    <Dots
                        content={content}
                        tmpl={dotTmpl}
                        step={this.step}
                        current={index}
                        extension={this.extension}
                        dotsAction={dotsAction}
                        dotStyle={dotStyle}
                        dotCurrentStyle={dotCurrentStyle}
                    />
                ) : null}
                <div className={styles.slider}>
                    <div
                        className={styles.container}
                        style={{
                            [sizeType]: (content.length + this.extension * 2) * unitSize,
                            [shiftingType]: shifting,
                        }}>
                        <List
                            content={content}
                            unitSize={unitSize}
                            extension={this.extension}
                            current={index}
                            tmpl={sliderTmpl}
                            axis={axis}
                        />
                    </div>
                </div>
            </div>
        ) : (
            <div ref={this.container} />
        );
    }
}

export default Slides;
